import type { DependencyList } from 'react';

import { WsEvent } from '../types/api/generated-enums';
import { logger } from '../utils/logger';

export interface IWebSocketConfig {
  url: string;
  getToken: () => string | Promise<string>;
  middleware?: (action: ISubscriptions) => ISubscriptions;
  onSubscribe?: (action: ISubscriptions) => void;
  onUnsubscribe?: (action: ISubscriptions) => void;
  onConnectionError?: (error: Error) => void;
}

export interface ISubscriptions {
  method: string;
  action: (...args: unknown[]) => void;
}

interface SocketPayload {
  method: string;
  args: unknown[];
}

const HEARTBEAT_INTERVAL_MS = 30_000;

export class WsConnection {
  private readonly config: IWebSocketConfig;
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectDelay = 1_000;
  private destroyed = false;
  private hasConnectedOnce = false;

  // method → Set of stable handler functions
  private readonly listeners = new Map<string, Set<(...args: unknown[]) => void>>();

  constructor(config: IWebSocketConfig) {
    this.config = config;
    void this.connect();
  }

  private async connect(): Promise<void> {
    if (this.destroyed) return;
    try {
      const token = await this.config.getToken();
      const ws = new WebSocket(`${this.config.url}?token=${token}`);
      this.socket = ws;

      ws.onopen = () => {
        this.reconnectDelay = 1_000;

        if (this.hasConnectedOnce) {
          this.dispatch(WsEvent.Reconnected, []);
        }
        this.hasConnectedOnce = true;

        this.heartbeatTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ method: WsEvent.Ping, args: [] }));
          }
        }, HEARTBEAT_INTERVAL_MS);
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        logger.log('[WS] onmessage raw:', event.data);
        try {
          const payload = JSON.parse(event.data) as SocketPayload;
          this.dispatch(payload.method, payload.args ?? []);
        } catch {}
      };

      ws.onerror = () => {
        this.config.onConnectionError?.(new Error('WebSocket error'));
      };

      ws.onclose = () => {
        this.clearHeartbeat();
        if (!this.destroyed) this.scheduleReconnect();
      };
    } catch (err) {
      this.config.onConnectionError?.(err instanceof Error ? err : new Error(String(err)));
      this.scheduleReconnect();
    }
  }

  private clearHeartbeat(): void {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private dispatch(method: string, args: unknown[]): void {
    this.listeners.get(method)?.forEach((fn) => fn(...args));
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null || this.destroyed) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30_000);
      void this.connect();
    }, this.reconnectDelay);
  }

  on(method: string, handler: (...args: unknown[]) => void): void {
    if (!this.listeners.has(method)) {
      this.listeners.set(method, new Set());
    }
    this.listeners.get(method)!.add(handler);
    this.config.onSubscribe?.({ method, action: handler });
  }

  off(method: string, handler: (...args: unknown[]) => void): void {
    const bucket = this.listeners.get(method);
    if (!bucket) return;
    bucket.delete(handler);
    if (bucket.size === 0) this.listeners.delete(method);
    this.config.onUnsubscribe?.({ method, action: handler });
  }

  disconnect(): void {
    this.destroyed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearHeartbeat();
    this.socket?.close();
    this.socket = null;
  }
}

export type { DependencyList };
